import { Component, Input, OnInit } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { PaginationService } from '@services/pagination/pagination.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { UpdateDiscountModal } from '@web-components/packages-product/modals/update-discount-modal/update-discount-modal.component';
import { Subscription } from 'rxjs/Subscription';
import {
	IItemsOverviewGen,
	IModalAction,
	IProductPackageItem,
	PageRequest
} from '@models/models';
import { Observable } from 'rxjs/Observable';

@Component({
	selector: 'discount-block',
	template: require('./discount-block.component.html')
})
export class DiscountBlockComponent implements OnInit {
	@Input() companyId: number;
	discounts: IProductPackageItem[] = [];
	pageRequest: PageRequest;
	itemsQuantity: number;
	pageNumbers: Array<number>;
	private allowedFilters: string[];

	readonly DISCOUNTS_REQUEST_DEFAULT: PageRequest = {
		filterBy: 'gifts',
		paging: {
			pageNumber: 0,
			pageSize: 50,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	readonly DISCOUNTS_DEFAULT: IProductPackageItem = {
		name: '',
		taxType: 'NO_TAX',
		type: 'GIFT',
		description: '<p>Discount</p>'
	};

	readonly MODAL_TITLES: string[] = ['Add New', 'Duplicate', 'Edit'];

	selectedDiscountModalRef: NgbModalRef;
	modalDataSubscription: Subscription;

	constructor(
		private productPackagesService: ProductPackagesDataServiceProvider,
		private messageService: MessageService,
		private paginationService: PaginationService,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		this.applyPageDiscountFromLocalStorage();
		this.getDiscountsData();
	}

	updateDiscountData(pageRequest: PageRequest) {
		this.pageRequest = pageRequest;
		this.getDiscountsData();
	}

	isFilterAllowed(filterName: string): boolean {
		if (!this.allowedFilters) {
			return false;
		}

		return this.allowedFilters.includes(filterName);
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getDiscountsData();
	}

	openAddNewDiscountModal(): void {
		this.selectedDiscountModalRef = this.modalService.openModal(
			UpdateDiscountModal
		);

		this.getModalDataForModals();

		this.selectedDiscountModalRef.componentInstance.modalAction = this.MODAL_TITLES[0];
		this.selectedDiscountModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(this.DISCOUNTS_DEFAULT)
		);
		this.selectedDiscountModalRef.componentInstance.btnDisabled = false;
	}

	openEditDiscountModal(discountItem: IProductPackageItem): void {
		this.selectedDiscountModalRef = this.modalService.openModal(
			UpdateDiscountModal
		);

		this.getModalDataForModals();

		this.selectedDiscountModalRef.componentInstance.modalAction = this.MODAL_TITLES[2];
		this.selectedDiscountModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(discountItem)
		);
		this.selectedDiscountModalRef.componentInstance.btnDisabled = false;
	}

	openDuplicateDiscountModal(discountItem: IProductPackageItem): void {
		this.selectedDiscountModalRef = this.modalService.openModal(
			UpdateDiscountModal
		);

		this.getModalDataForModals();

		this.selectedDiscountModalRef.componentInstance.modalAction = this.MODAL_TITLES[1];
		this.selectedDiscountModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(discountItem)
		);
		this.selectedDiscountModalRef.componentInstance.btnDisabled = false;
	}

	openDeleteDiscountModal(discountItem: IProductPackageItem): void {
		this.selectedDiscountModalRef = this.modalService.openModal(
			DeleteItemModalComponent
		);

		this.getModalDataForModals();

		this.selectedDiscountModalRef.componentInstance.modalTitle = 'Discount';
		this.selectedDiscountModalRef.componentInstance.btnDisabled = false;
		this.selectedDiscountModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(discountItem)
		);
		this.selectedDiscountModalRef.componentInstance.itemName =
			discountItem.name;
	}

	private getModalDataForModals(): void {
		this.selectedDiscountModalRef.result.then(
			(): void => this.modalDataSubscription.unsubscribe(),
			(): void => this.modalDataSubscription.unsubscribe()
		);

		this.modalDataSubscription = this.modalService
			.getModalData()
			.filter(data => data && data.action && data.props)
			.subscribe((data: IModalAction<IProductPackageItem>) =>
				this.discountActions(data)
			);
	}

	private discountActions({
		action,
		props
	}: IModalAction<IProductPackageItem>): void {
		this.selectedDiscountModalRef.componentInstance.btnDisabled = true;

		switch (action) {
			case 'Add New':
				this.addNewDiscount(props).subscribe(
					(data: IProductPackageItem): void =>
						this.messageService.showToaster(
							's_12',
							'productsPackages',
							data.name
						)
				);
				break;
			case 'Duplicate':
				this.addNewDiscount(props).subscribe(
					(data: IProductPackageItem): void =>
						this.messageService.showToaster(
							's_14',
							'productsPackages',
							data.name
						)
				);
				break;
			case 'Edit':
				this.updateDiscount(props).subscribe(
					(data: IProductPackageItem): void =>
						this.messageService.showToaster(
							's_13',
							'productsPackages',
							data.name
						)
				);
				break;
			case 'Delete':
				this.deleteDiscount(props).subscribe((): void =>
					this.messageService.showToaster(
						's_11',
						'productsPackages',
						props.name
					)
				);
				break;
			default:
				break;
		}
	}

	private updateDiscount(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesService
			.updatePackagesOrProducts(dataToSend.id, dataToSend)
			.map((data: any) => this.resetDiscountDataAfterAction(data));
	}

	private addNewDiscount(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesService
			.createPackagesOrProducts(dataToSend)
			.map((data: any) => this.resetDiscountDataAfterAction(data));
	}

	private deleteDiscount(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesService
			.deletePackagesOrProducts(dataToSend.id)
			.map((data: any) => this.resetDiscountDataAfterAction(data));
	}

	private resetDiscountDataAfterAction(
		data: IProductPackageItem
	): IProductPackageItem {
		this.pageRequest.paging.searchToken = null;
		this.pageRequest.paging.pageNumber = 0;
		this.getDiscountsData();
		this.modalDataSubscription.unsubscribe();
		this.selectedDiscountModalRef.componentInstance.btnDisabled = false;
		this.selectedDiscountModalRef.close();
		return data;
	}

	private getDiscountsData(): void {
		this.productPackagesService
			.getPackagesOrProducts(this.pageRequest)
			.subscribe((data: IItemsOverviewGen<IProductPackageItem>) =>
				this.successGetDiscountsData(data)
			);
	}

	private successGetDiscountsData(
		data: IItemsOverviewGen<IProductPackageItem>
	): void {
		this.discounts = data.items;
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pageNumbers = this.paginationService.calculatePageNumbers(
			this.itemsQuantity,
			this.pageRequest.paging.pageSize
		);
		this.pageRequest.paging.searchToken = data.searchToken;
		this.allowedFilters = data.sortingFields;
		this.setPageDiscountToLocalStorage(this.pageRequest);
	}

	// LOCAL STORAGE
	private applyPageDiscountFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`settingsDiscountsRequest_${this.companyId}`)
		);

		this.pageRequest = this.DISCOUNTS_REQUEST_DEFAULT;

		if (pageRequestObject && pageRequestObject !== '') {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPageDiscountToLocalStorage(pageRequest: PageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`settingsDiscountsRequest_${this.companyId}`);
		localStorage.setItem(
			`settingsDiscountsRequest_${this.companyId}`,
			pageRequestString
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/packages-product/discount-block/discount-block.component.ts